import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
};

// 콘텐츠는 기본적으로 항상 보이며, IntersectionObserver가 동작하는
// 환경에서만 숨김 → 페이드업 애니메이션을 적용한다.
export function Reveal({ children, className, delay = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined' || window.innerHeight === 0) {
      return undefined;
    }

    element.classList.add('tm-reveal');

    // IO가 콜백을 전혀 전달하지 않는 환경(일부 웹뷰/헤드리스)에서는
    // 숨김을 해제해 콘텐츠가 가려지지 않게 한다.
    let delivered = false;

    const observer = new IntersectionObserver(
      (entries) => {
        delivered = true;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(element);

    const fallback = window.setTimeout(() => {
      if (!delivered) {
        element.classList.remove('tm-reveal');
        observer.disconnect();
      }
    }, 800);

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
      element.classList.remove('tm-reveal');
    };
  }, []);

  return (
    <div ref={ref} className={className} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
}
